/**
 * Runs `worker` over every item with at most `limit` in flight at once, returning the
 * results in input order. A rejected worker never stops the rest - each result carries
 * either its value or its error so callers can report partial success.
 */
export type ConcurrencyResult<TItem, TResult> =
  | { item: TItem; ok: true; value: TResult }
  | { item: TItem; ok: false; error: unknown };

export default async function runWithConcurrency<TItem, TResult>(
  items: TItem[],
  limit: number,
  worker: (item: TItem) => Promise<TResult>,
  onProgress?: (completed: number, total: number) => void
): Promise<ConcurrencyResult<TItem, TResult>[]> {
  const results: ConcurrencyResult<TItem, TResult>[] = new Array(items.length);
  let next = 0;
  let completed = 0;

  const runLane = async () => {
    while (next < items.length) {
      const index = next++;
      const item = items[index];

      try {
        results[index] = { item, ok: true, value: await worker(item) };
      } catch (error) {
        results[index] = { item, ok: false, error };
      }

      completed += 1;
      onProgress?.(completed, items.length);
    }
  };

  const lanes = Math.max(1, Math.min(limit, items.length));
  await Promise.all(Array.from({ length: lanes }, runLane));

  return results;
}
